import { ScreenType, useScreenType } from "@redotech/react-util/screen";
import { FeatureOnboardingType } from "@redotech/redo-model/onboarding/user-onboarding";
import { assertNever } from "@redotech/util/type";
import { memo, useMemo } from "react";
import * as styles from "./index.module.css";
import { AgenticCatalogOnboarding } from "./onboarding-cards/agentic-catalog-onboarding";
import { BillingSettingsConsolidatedOnboarding } from "./onboarding-cards/billing-settings-consolidated";
import { ConversionOnboarding } from "./onboarding-cards/conversion-onboarding";
import { ExpandedWarrantyOnboarding } from "./onboarding-cards/expanded-warranty-onboarding";
import { HelpIconMovingOnboarding } from "./onboarding-cards/help-icon-moving";
import { IntercomMovedOnboarding } from "./onboarding-cards/intercom-moved";
import { ManagedReturnsOnboarding } from "./onboarding-cards/managed-returns";
import { PackageProtectionPlusOnboarding } from "./onboarding-cards/package-protection-plus";
import { RedoCommentsold } from "./onboarding-cards/redo-commentsold";
import { AiSupportActionNeededV2Modal } from "./onboarding-modals/ai-support-action-needed-v2-modal";
import { OutboundLabelsUpsellOnboarding } from "./onboarding-modals/outbound-labels-upsell";
import { ShopperAiWrappedModal } from "./onboarding-modals/shopper-ai-wrapped-modal";
import { useFeatureOnboardingManager } from "./use-feature-onboarding";

export const FeatureOnboarding = memo(function FeatureOnboarding() {
  const screenType = useScreenType();
  const { onboardingType, showOnboarding, handleDismiss, handleComplete } =
    useFeatureOnboardingManager();

  const card = useMemo(() => {
    if (screenType === ScreenType.MOBILE) {
      return null;
    }
    if (
      !onboardingType ||
      !showOnboarding ||
      onboardingType === FeatureOnboardingType.DISABLED
    ) {
      return null;
    }
    switch (onboardingType) {
      case FeatureOnboardingType.PACKAGE_PROTECTION_PLUS:
        return (
          <PackageProtectionPlusOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.EXPANDED_WARRANTY:
        return (
          <ExpandedWarrantyOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.MANAGED_RETURNS:
        return (
          <ManagedReturnsOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.CONVERSION:
        return (
          <ConversionOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.INTERCOM_MOVED:
        return (
          <IntercomMovedOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SUPPORT_TO_AI_UPSELL:
        return (
          <AiSupportActionNeededV2Modal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.COMMENTSOLD_REDO:
        return (
          <RedoCommentsold
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.SHOPPER_AI_WRAPPED:
        return (
          <ShopperAiWrappedModal
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.BILLING_SETTINGS_CONSOLIDATED:
        return (
          <BillingSettingsConsolidatedOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.HELP_ICON_MOVING:
        return (
          <HelpIconMovingOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.AGENTIC_CATALOG:
        return (
          <AgenticCatalogOnboarding
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_UPS:
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_AMAZON:
      case FeatureOnboardingType.OUTBOUND_LABELS_UPSELL_DHL:
        return (
          <OutboundLabelsUpsellOnboarding
            carrier={onboardingType}
            handleComplete={handleComplete}
            handleDismiss={handleDismiss}
          />
        );
      // Historical onboarding types - no longer actively used
      case FeatureOnboardingType.OUTBOUND_VIEWS_MOVED:
      case FeatureOnboardingType.DISPUTES_WELCOME:
      case FeatureOnboardingType.AI_EXCHANGE_AGENT_UPSELL:
      case FeatureOnboardingType.INTERNATIONAL_RETURNS:
      case FeatureOnboardingType.ROLES_PERMISSIONS_UPDATE:
      case FeatureOnboardingType.MARKETING_TEXAS_SMS_PROTECTION:
      case FeatureOnboardingType.BLACK_FRIDAY_TIPS:
      case FeatureOnboardingType.PERMISSIONS_ROLES:
      case FeatureOnboardingType.OUTBOUND_LABELS:
        return null;
      default:
        assertNever(onboardingType);
    }
  }, [
    handleComplete,
    handleDismiss,
    onboardingType,
    showOnboarding,
    screenType,
  ]);

  if (!card) {
    return null;
  }

  return <div className={styles.container}>{card}</div>;
});

export const CROSS_SELL_POPUP_URL =
  "https://assets.getredo.com/cross-sell-popup";
